import { useState, useCallback, useRef, useEffect } from 'react';

interface UseTextToSpeechOptions {
  rate?: number;
  pitch?: number;
  volume?: number;
  voiceIndex?: number;
}

interface VoiceInfo extends SpeechSynthesisVoice {
  gender?: 'male' | 'female';
}

export const useTextToSpeech = (options: UseTextToSpeechOptions = {}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeechSupported] = useState(() => {
    return typeof window !== 'undefined' && ('speechSynthesis' in window);
  });
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Detect voice gender based on name patterns
  const getVoiceGender = (voiceName: string): 'male' | 'female' | 'unknown' => {
    const name = voiceName.toLowerCase();
    
    // Female patterns
    if (name.includes('female') || name.includes('woman') || name.includes('girl') || 
        name.includes('victoria') || name.includes('samantha') || name.includes('karen') ||
        name.includes('moira') || name.includes('zira') || name.includes('susan') ||
        name.includes('hazel') || name.includes('fiona') || name.includes('catherine')) {
      return 'female';
    }
    
    // Male patterns
    if (name.includes('male') || name.includes('man') || name.includes('boy') ||
        name.includes('david') || name.includes('mark') || name.includes('george') ||
        name.includes('tom') || name.includes('james') || name.includes('henry') ||
        name.includes('albert') || name.includes('daniel')) {
      return 'male';
    }
    
    return 'unknown';
  };

  // Load available voices
  useEffect(() => {
    if (!isSpeechSupported) return;

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      setAvailableVoices(voices);
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null as any;
    };
  }, [isSpeechSupported]);

  const speak = useCallback((text: string, voiceIndex?: number) => {
    if (!isSpeechSupported) {
      console.warn('Text-to-speech not supported in this browser');
      return;
    }

    // Stop any ongoing speech
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }

    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = options.rate ?? 1;
    utterance.pitch = options.pitch ?? 1;
    utterance.volume = options.volume ?? 1;
    
    // Set voice if available
    const effectiveVoiceIndex = voiceIndex ?? options.voiceIndex;
    if (effectiveVoiceIndex !== undefined && availableVoices[effectiveVoiceIndex]) {
      utterance.voice = availableVoices[effectiveVoiceIndex];
    }

    utterance.onstart = () => {
      setIsPlaying(true);
    };

    utterance.onend = () => {
      setIsPlaying(false);
    };

    utterance.onerror = () => {
      setIsPlaying(false);
    };

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [isSpeechSupported, isPlaying, options, availableVoices]);

  const stop = useCallback(() => {
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
    }
  }, [isPlaying]);

  // Get voices filtered by gender
  const getVoicesByGender = (gender: 'male' | 'female') => {
    return availableVoices
      .map((voice, idx) => ({
        voice,
        idx,
        gender: getVoiceGender(voice.name)
      }))
      .filter(({ gender: voiceGender }) => voiceGender === gender)
      .map(({ voice, idx }) => ({ voice, idx }));
  };

  // Get voices filtered by language
  const getVoicesByLanguage = (langCode: string) => {
    return availableVoices
      .map((voice, idx) => ({ voice, idx }))
      .filter(({ voice }) => voice.lang.startsWith(langCode));
  };

  // Get voices filtered by gender and language
  const getVoicesByGenderAndLanguage = (gender: 'male' | 'female', langCode: string) => {
    return availableVoices
      .map((voice, idx) => ({
        voice,
        idx,
        voiceGender: getVoiceGender(voice.name)
      }))
      .filter(({ voiceGender, voice }) => 
        voiceGender === gender && voice.lang.startsWith(langCode)
      )
      .map(({ voice, idx }) => ({ voice, idx }));
  };

  return {
    speak,
    stop,
    isPlaying,
    isSpeechSupported,
    availableVoices,
    getVoiceGender,
    getVoicesByGender,
    getVoicesByLanguage,
    getVoicesByGenderAndLanguage,
  };
};
